import { CAPA_EPS } from '../constants';
import { DifTpCd, ElecDiffRow, ElecRowVals, ElecSourceRow } from '../types';

/** null/undefined-safe 문자열 비교용 */
const s = (v: unknown): string => (v == null ? '' : String(v));

const groupBy = <T>(rows: T[], fn: (r: T) => string): Map<string, T[]> => {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const k = fn(r);
    const list = m.get(k);
    if (list) list.push(r);
    else m.set(k, [r]);
  }
  return m;
};

/** 코드+Line+전압유형 그룹핑 키 */
const grpKey = (r: ElecSourceRow) => `${r.stndEqpId}||${r.prdnLnNm}||${r.vt}`;

/** 전/후 값 비교로 변경 태그 목록 산출 */
const detectTags = (b: ElecSourceRow | null, a: ElecSourceRow | null): string[] => {
  const tags: string[] = [];
  if (!b || !a) return tags;
  if (s(b.brkeCapa) !== s(a.brkeCapa)) tags.push('차단기변경');
  if (b.elpwEqpQty !== a.elpwEqpQty) tags.push('Qty변경');
  if (Math.abs((b.elpwSumCapa ?? 0) - (a.elpwSumCapa ?? 0)) >= CAPA_EPS) tags.push('Capacity변경');
  if (s(b.dmdElpwTarfNvl) !== s(a.dmdElpwTarfNvl)) tags.push('DF변경');
  if (s(b.elpwEqpCnctTpCd) !== s(a.elpwEqpCnctTpCd)) tags.push('ConnType변경');
  const lc: string[] = [];
  const locPairs: [string, string, string][] = [
    ['bizAreaNm', 'AREA', ''],
    ['bldgFloCd', 'Floor', ''],
    ['istlLocNm', 'Room', ''],
    ['eqpMainNm', 'Main', ''],
    ['eqpMchNm', 'Machine', ''],
  ];
  for (const [field, label] of locPairs) {
    const bv = (b as unknown as Record<string, unknown>)[field];
    const av = (a as unknown as Record<string, unknown>)[field];
    if (bv && av && bv !== av) lc.push(label);
  }
  if (lc.length) tags.push(`위치변경(${lc.join('·')})`);
  return tags;
};

/** 태그 목록 → 표시 라벨 ("일치" | "변경(Qty변경+위치변경(Machine명))" 등) */
const buildTypeLabel = (
  tags: string[],
  b: ElecSourceRow | null,
  a: ElecSourceRow | null
): string => {
  if (!tags.length) return '일치';
  const onlyMachine = tags.length === 1 && tags[0] === '위치변경(Machine)';
  if (onlyMachine) {
    const mname = a?.eqpMchNm || b?.eqpMchNm || 'Machine변경';
    return `변경(${mname})`;
  }
  const formatted = tags.map((t) => {
    if (t === '위치변경(Machine)') {
      const mname = a?.eqpMchNm || b?.eqpMchNm || 'Machine변경';
      return `위치변경(${mname})`;
    }
    return t;
  });
  return `변경(${formatted.join('+')})`;
};

const difTpCdOf = (label: string): DifTpCd => {
  if (label === '일치') return 'SAME';
  if (label === '추가') return 'ADD';
  if (label === '삭제') return 'DEL';
  return 'CHG';
};

interface WorkRow {
  key: string;
  label: string;
  b: ElecSourceRow | null;
  a: ElecSourceRow | null;
}

const toDiffRow = (w: WorkRow): ElecDiffRow => ({
  key: w.key,
  difTpCd: difTpCdOf(w.label),
  difTpCtn: w.label,
  before: w.b,
  after: w.a,
});

/**
 * 전/후 전기 원천 데이터를 코드(표준설비ID)+Line+전압유형 키로 1:1 매핑해
 * 일치/변경/추가/삭제로 분류한다. HTML 툴(UT_Matrix_비교분석_Rev15.html) runAnalysis() 포팅.
 */
export const compareElec = (before: ElecSourceRow[], after: ElecSourceRow[]): ElecDiffRow[] => {
  const mapB = groupBy(before, grpKey);
  const mapA = groupBy(after, grpKey);
  // target es5 + downlevelIteration 꺼짐 — Map/Set 이터레이터는 Array.from으로 편다(TS2802)
  const allKeys = new Set<string>(Array.from(mapB.keys()).concat(Array.from(mapA.keys())));

  let rows: WorkRow[] = [];
  for (const k of Array.from(allKeys)) {
    const bList = mapB.get(k) ?? [];
    const aList = mapA.get(k) ?? [];
    const maxLen = Math.max(bList.length, aList.length);
    for (let i = 0; i < maxLen; i += 1) {
      const b = bList[i] ?? null;
      const a = aList[i] ?? null;
      let label: string;
      if (!b) label = '추가';
      else if (!a) label = '삭제';
      else {
        const tags = detectTags(b, a);
        label = tags.length ? buildTypeLabel(tags, b, a) : '일치';
      }
      rows.push({ key: `${k}||${i}`, label, b, a });
    }
  }

  // ── Line변경 감지: 삭제 행과 추가 행 중 코드+전압유형이 같고 용량이 비슷한 것끼리 재매칭 ──
  const delRows = rows.filter((r) => r.label === '삭제');
  const addRows = rows.filter((r) => r.label === '추가');
  const toRemove = new Set<string>();
  const usedA = new Set<string>();
  const lineChanged: WorkRow[] = [];
  for (const dr of delRows) {
    const [code, , vt] = dr.key.split('||');
    const candidates = addRows.filter((ar) => {
      if (usedA.has(ar.key)) return false;
      const [ac, , avt] = ar.key.split('||');
      return ac === code && avt === vt;
    });
    if (candidates.length === 1) {
      const ar = candidates[0];
      const bCap = dr.b?.elpwSumCapa ?? 0;
      const aCap = ar.a?.elpwSumCapa ?? 0;
      if (Math.abs(bCap - aCap) < Math.max(bCap * 0.5, 1)) {
        toRemove.add(dr.key);
        toRemove.add(ar.key);
        usedA.add(ar.key);
        const tags = ['Line변경', ...detectTags(dr.b, ar.a)];
        lineChanged.push({
          key: dr.key,
          label: buildTypeLabel(tags, dr.b, ar.a),
          b: dr.b,
          a: ar.a,
        });
      }
    }
  }
  rows = [...rows.filter((r) => !toRemove.has(r.key)), ...lineChanged];

  // ── 그룹 단위 실질 동일 판정: 개별 행은 변경/삭제/추가로 잡혔어도 그룹 전체
  // (같은 코드+Line+vt) 의 Qty합/Cap_Sum합/수용율합/차단기 목록이 전후 동일하면 "일치"로 재분류 ──
  const grpMap = new Map<string, WorkRow[]>();
  for (const r of rows) {
    const gk = r.key.split('||').slice(0, 3).join('||');
    const list = grpMap.get(gk);
    if (list) list.push(r);
    else grpMap.set(gk, [r]);
  }
  for (const glist of Array.from(grpMap.values())) {
    if (glist.every((r) => r.label === '일치')) continue;
    if (glist.some((r) => r.label === '삭제' || r.label === '추가')) continue;
    const qb = glist.reduce((sum, r) => sum + (r.b?.elpwEqpQty ?? 0), 0);
    const qa = glist.reduce((sum, r) => sum + (r.a?.elpwEqpQty ?? 0), 0);
    const cb = glist.reduce((sum, r) => sum + (r.b?.elpwSumCapa ?? 0), 0);
    const ca = glist.reduce((sum, r) => sum + (r.a?.elpwSumCapa ?? 0), 0);
    const db = glist.reduce((sum, r) => sum + (r.b?.dmdElpwCapa ?? 0), 0);
    const da = glist.reduce((sum, r) => sum + (r.a?.dmdElpwCapa ?? 0), 0);
    const bb = glist
      .map((r) => r.b?.brkeCapa ?? 0)
      .sort((x, y) => x - y)
      .join(',');
    const ba = glist
      .map((r) => r.a?.brkeCapa ?? 0)
      .sort((x, y) => x - y)
      .join(',');
    if (qb === qa && Math.abs(cb - ca) < CAPA_EPS && Math.abs(db - da) < CAPA_EPS && bb === ba) {
      for (const r of glist) {
        r.label = '일치';
        if (r.b && r.a) r.a = { ...r.b };
      }
    }
  }

  const order: Record<string, number> = { 추가: 0, 삭제: 3, 일치: 4 };
  rows.sort((x, y) => {
    const ox = x.label.startsWith('변경') ? 1 : order[x.label] ?? 2;
    const oy = y.label.startsWith('변경') ? 1 : order[y.label] ?? 2;
    return ox - oy;
  });

  return rows.map(toDiffRow);
};

// ─── 종합 분석 3항목 규칙 기반 생성 (A-0001~A-0003) ─────────────────────
//
// ref/frontend/features/UtComparisonElec/utils/compareElec.ts에서 그대로 옮겼다.
// 전기는 AI를 쓰지 않고 아래 규칙으로 텍스트를 만들어 TB_EELMB_UTM_RPT_OPIN_M에
// 저장한다(ref/README.md §4 "종합 분석 3항목 자동 생성").
// 입력은 항상 **기준(before) UT Matrix의 원천 행 전체**다.

/**
 * 설비 1건의 차단기 여유율 판정 — 부족/적정/과다.
 *
 * K(정격전류) = ELPW_CAPA(kW) × 1000 / 역률 / 전압, 3P 등은 √3(1.732)로 한 번 더 나눈다.
 * ⚠ kW→W 환산(×1000)을 빠뜨리면 K가 1000배 작아져 전부 "과다"로만 나온다
 *   (ref 개발 중 실측 데이터로 발견된 버그).
 *
 * 저장된 이력을 복원한 행은 원본 역율/전압이 없어 재계산이 불가능하므로,
 * brkAnlsCtn(저장된 문구)이 있으면 그대로 쓴다.
 */
export const classifyBreaker = (row: ElecSourceRow | null): string => {
  if (!row) return '-';
  if (row.brkAnlsCtn) return row.brkAnlsCtn;
  const { brkeCapa, elpwCapa, pwftNvl, vltgNvl, elpwPhasCd } = row;
  if (brkeCapa == null || !elpwCapa || !pwftNvl || !vltgNvl) return '-';
  const denom = elpwPhasCd === '1P' ? pwftNvl * vltgNvl : pwftNvl * vltgNvl * 1.732;
  if (!denom) return '-';
  const k = (elpwCapa * 1000) / denom;
  if (brkeCapa < k * 1.25) return '부족';
  if (brkeCapa <= k * 1.5) return '적정';
  return '과다';
};

/**
 * 저장용 — 각 행의 차단기 판정(부족/적정/과다)을 before/after에 채워 넣는다.
 *
 * 판정에 필요한 역률(PWFT_NVL)·전압은 원천 UT Matrix에만 있고 RPT_ELPW_D에는 저장되지
 * 않는다. 그래서 저장 시점에 계산해 문구를 함께 넣어두지 않으면, 나중에 리포트를 열었을 때
 * 다시 판정할 방법이 없다 — BF_BRKE_ANLS_CTN / AF_BRKE_ANLS_CTN 컬럼이 있는 이유다.
 *
 * 판정 불가('-')는 넣지 않는다. 집계 쿼리가 부족/적정/과다만 세므로 NULL로 두는 편이
 * "판정 못 함"과 "값 없음"을 구분하지 않아도 되어 단순하다.
 */
export const withBreakerAnalysis = (rows: ElecDiffRow[]): ElecDiffRow[] => {
  const judge = (row: ElecSourceRow | null): ElecSourceRow | null => {
    if (!row) return null;
    const ctn = classifyBreaker(row);
    return ctn === '-' ? row : { ...row, brkAnlsCtn: ctn };
  };
  return rows.map((row) => ({ ...row, before: judge(row.before), after: judge(row.after) }));
};

/**
 * A-0001 "차단기 여유율 분석" — 기준 UT Matrix 전체 설비를 1P 단상 / 단상 외로 나눠
 * 부족·적정·과다 건수를 집계한다.
 */
export const buildBreakerMarginSummary = (baseRows: ElecSourceRow[]): string => {
  const singlePhase = baseRows.filter((r) => r.elpwPhasCd === '1P');
  const other = baseRows.filter((r) => r.elpwPhasCd !== '1P');
  const count = (rows: ElecSourceRow[]) => {
    let short = 0;
    let ok = 0;
    let over = 0;
    rows.forEach((r) => {
      const c = classifyBreaker(r);
      if (c === '부족') short += 1;
      else if (c === '적정') ok += 1;
      else if (c === '과다') over += 1;
    });
    return { short, ok, over };
  };
  const c1 = count(singlePhase);
  const c2 = count(other);
  return (
    `1. 1P 단상 총 : ${singlePhase.length}건\n` +
    `   부족 : ${c1.short}건, 적정 : ${c1.ok}건, 과다 : ${c1.over}건\n` +
    `2. 단상 외 총 : ${other.length}건\n` +
    `   부족 : ${c2.short}건, 적정 : ${c2.ok}건, 과다 : ${c2.over}건`
  );
};

/** BIZ_AREA_NM이 비어있는 설비를 묶는 이름 — 제외하지 않고 함께 집계한다 */
const UNASSIGNED_AREA = '미설정';

/**
 * A-0002 "Area별 전압 통합화 제안" — AREA(BIZ_AREA_NM)별 전압(VLTG_NVL)별로 설비수량
 * (ELPW_EQP_QTY)을 합산해, 대수가 가장 많은 전압으로 나머지 전압 설비의 변경을 제안한다
 * (임계치 없이 항상 최다수량 전압 기준).
 */
export const buildVoltageUnifySummary = (baseRows: ElecSourceRow[]): string => {
  const byArea = new Map<string, Map<number, number>>();
  baseRows.forEach((r) => {
    const area = r.bizAreaNm?.trim() || UNASSIGNED_AREA;
    if (r.vltgNvl == null) return;
    const voltMap = byArea.get(area) ?? new Map<number, number>();
    voltMap.set(r.vltgNvl, (voltMap.get(r.vltgNvl) ?? 0) + r.elpwEqpQty);
    byArea.set(area, voltMap);
  });

  // 미설정은 항상 맨 뒤, 그 외는 숫자 Area면 숫자순, 아니면 가나다순
  const areaNames = Array.from(byArea.keys()).sort((a, b) => {
    if (a === UNASSIGNED_AREA) return 1;
    if (b === UNASSIGNED_AREA) return -1;
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.localeCompare(b, 'ko');
  });

  const lines: string[] = [];
  areaNames.forEach((area) => {
    const voltMap = byArea.get(area);
    if (!voltMap || voltMap.size < 2) return; // 전압이 하나뿐이면 통합 제안 불필요
    const entries = Array.from(voltMap.entries()).sort((a, b) => b[0] - a[0]); // 전압 내림차순
    const [majorVolt] = entries.reduce((max, cur) => (cur[1] > max[1] ? cur : max));
    const minorVolts = entries.filter(([v]) => v !== majorVolt).map(([v]) => v);
    if (minorVolts.length === 0) return;
    const listStr = entries.map(([v, q]) => `${v}V 설비 : ${q}대`).join(', ');
    const minorStr = minorVolts.map((v) => `${v}V`).join(', ');
    lines.push(`${area} AREA에 ${listStr}  ${minorStr} 설비의 전압을 ${majorVolt}V로 변경 제안`);
  });

  if (lines.length === 0) return '통합 제안 대상이 없습니다.';
  return lines.map((l, i) => `${i + 1}. ${l}`).join('\n');
};

/** 전기 종합 분석 항목코드 — 공통코드 ANLS_OPIN_TP_CD(OPT_VAL_CTN1='ELEC') */
export const ELEC_OPIN_TP_CDS = ['A-0001', 'A-0002', 'A-0003', 'A-0004'] as const;

/**
 * FE가 만들지 않는 항목 — BE가 리포트 생성 시 직접 채운다.
 *
 * A-0003 "훅업 포인트 간소화"는 원천 테이블(TB_EELMB_UTM_D + TGT_PROC_M + 공통코드)을
 * 직접 조인해야 기준 쿼리와 같은 모수가 나오는데, 화면이 쓰는 조회 API로는 그 모수를
 * 그대로 재현할 수 없어 BE 생성으로 정한다.
 * → pages/ut/ref/BE-REQUEST-elec-hookup-simplify.md
 *
 * 아래 buildElecAnalysisTexts가 이 코드를 만들지 않으므로, saveRuleBasedOpinions의
 * 덮어쓰기 대상에서도 빠져 BE가 넣어둔 행이 그대로 보존된다.
 */
export const ELEC_OPIN_TP_CDS_BE = ['A-0003'] as const;

/**
 * AI가 만드는 항목 — 본문이 `AI_ANS_CTN`에 들어간다(나머지 3항목은 `ANLS_RLT_CTN`).
 *
 * A-0004 "전력 사용량 종합분석"은 리포트 생성 직후 requestAiDraftOpinions가
 * `/v1/ut/ai-ref/report`를 report_domain='elec'으로 호출하고, BE가 응답을 파싱해 저장한다.
 * 화면은 ElecOpinionPanel이 aiFieldCodes로 넘겨 컬럼과 진행 표시를 가른다.
 * → pages/ut/ref/BE-REQUEST-elec-ai-power-analysis.md
 *
 * buildElecAnalysisTexts가 이 코드를 만들지 않으므로 saveRuleBasedOpinions의 덮어쓰기
 * 대상에서도 빠져, BE/AI가 넣어둔 행이 그대로 보존된다(A-0003과 같은 경로).
 *
 * 프롭 타입이 string[]이라 as const로 두지 않는다(readonly 배열은 대입되지 않는다).
 */
export const ELEC_AI_OPIN_TP_CDS: string[] = ['A-0004'];

/**
 * 전기 종합 분석 중 **FE가 만드는 항목**을 한 번에 생성한다.
 * sortOrd는 공통코드 정렬순서와 맞춘다(A-0003은 BE가 sortOrd 3으로 넣는다).
 */
export const buildElecAnalysisTexts = (
  baseRows: ElecSourceRow[]
): { anlsOpinTpCd: string; sortOrd: number; text: string }[] => [
  { anlsOpinTpCd: 'A-0001', sortOrd: 1, text: buildBreakerMarginSummary(baseRows) },
  { anlsOpinTpCd: 'A-0002', sortOrd: 2, text: buildVoltageUnifySummary(baseRows) },
];

const fmt1 = (v: number | null | undefined): string => (v == null ? '-' : v.toFixed(1));

/** ElecDiffRow → 상세표/차단기 팝업에 표시할 평탄화된 값 (HTML 툴 getRowVals() 포팅) */
export const getRowVals = (row: ElecDiffRow): ElecRowVals => {
  const { before: b, after: a } = row;
  const loc = a ?? b;
  const voltage = a?.vt && a.vt !== 'UNKNOWN' ? a.vt : b?.vt && b.vt !== 'UNKNOWN' ? b.vt : '-';
  return {
    difTpCtn: row.difTpCtn,
    prdnProcNm: loc?.prdnProcNm || '-',
    eqclNm: loc?.eqclNm || '-',
    dtalProcNm: loc?.dtalProcNm || '-',
    bizAreaNm: loc?.bizAreaNm || '-',
    bldgFloCd: loc?.bldgFloCd || '-',
    istlLocNm: loc?.istlLocNm || '-',
    lnBf: b?.prdnLnNm || '-',
    lnAf: a?.prdnLnNm || '-',
    eqpMainNm: loc?.eqpMainNm || '-',
    eqpMchNm: loc?.eqpMchNm || '-',
    qtyBf: b ? b.elpwEqpQty : '',
    qtyAf: a ? a.elpwEqpQty : '',
    voltage,
    brkBf: b?.brkeCapa != null ? `${b.brkeCapa}A` : '-',
    brkAf: a?.brkeCapa != null ? `${a.brkeCapa}A` : '-',
    capBf: fmt1(b?.elpwCapa),
    capAf: fmt1(a?.elpwCapa),
    csumBf: fmt1(b?.elpwSumCapa),
    csumAf: fmt1(a?.elpwSumCapa),
    dfBf: b?.dmdElpwTarfNvl != null ? String(b.dmdElpwTarfNvl) : '-',
    dfAf: a?.dmdElpwTarfNvl != null ? String(a.dmdElpwTarfNvl) : '-',
    dmdCapaBf: fmt1(b?.dmdElpwCapa),
    dmdCapaAf: fmt1(a?.dmdElpwCapa),
    connType: a?.elpwEqpCnctTpCd || b?.elpwEqpCnctTpCd || '-',
    // 이력 복원 행은 역률·전압이 없어 재계산이 안 되므로 저장해둔 문구를 그대로 쓴다
    // (classifyBreaker가 row.brkAnlsCtn을 먼저 본다)
    brkAnlsCtnBf: classifyBreaker(b),
    brkAnlsCtnAf: classifyBreaker(a),
  };
};
